import { Request } from "./request";

const ARIA_DESC_BY = "aria-describedby";
const BLUR_EVENT = "blur";
const BUTTON_TYPE = "button";
const CANNOT_VALIDATE = "Unable to determine validity";
const EMAIL_TYPE = "email";
const EMAIL_TYPE_MISMATCH = "Please enter an email address.";
const EMPTY_MSG = "";
const ERROR_MESSAGE_CLASSNAME = "invalid-feedback";
const FILE_TYPE = "file";
const FORM_REQUIRED = "Form element required";
const FORM_SUBMIT_ENDPOINT = "/";
const FORM_TAG = "form";
const INPUT_ERROR_CLASS = "is-invalid";
const INPUT_TAG = "input";
const INVALID_FIELD = "Invalid field";
const INVALID_VALUE = "The value you entered for this field is invalid.";
const MAX_LENGTH = "maxLength";
const MIN_LENGTH = "minLength";
const NO_VALIDATE_ATTR = "novalidate";
const NUMERIC_ONLY = "Please enter a number.";
const PATTERN_MISMATCH = "Please match the requested format.";
const RESET_TYPE = "reset";
const SUBMIT_EVENT = "submit";
const SUBMIT_ERROR_HAS_ERROR = "has-error";
const SUBMIT_ERROR_SELECTOR = "submit-error";
const SUBMIT_SUCCESS_COMPLETE = "complete";
const SUBMIT_SUCCESS_SELECTOR = "submit-success";
const SUBMIT_TYPE = "submit";
const TITLE_ATTR = "title";
const TOKEN_ENDPOINT = "/csrf";
const VALUE_MISSING_ERR = "Please fill out this field.";
const CLICK_EVENT = "click";

function isForm(elem) {
  return !!elem && elem.tagName.toLowerCase() === FORM_TAG;
}

function isFunc(f) {
  return typeof f === "function";
}

function isValidatableType(type) {
  return (
    // !!type &&
    type !== FILE_TYPE &&
    type !== RESET_TYPE &&
    type !== SUBMIT_TYPE &&
    type !== BUTTON_TYPE
  );
}

export class FormValidator {
  static get FORM_SUBMIT_ENDPOINT() {
    return FORM_SUBMIT_ENDPOINT;
  }

  static get INPUT_ERROR_CLASS() {
    return INPUT_ERROR_CLASS;
  }

  static get NO_VALIDATE_ATTR() {
    return NO_VALIDATE_ATTR;
  }

  static get ERROR_MESSAGE_CLASSNAME() {
    return ERROR_MESSAGE_CLASSNAME;
  }

  static get TOKEN_ENDPOINT() {
    return TOKEN_ENDPOINT;
  }

  static get VALIDITY_STATE() {
    return {
      badInput: false,
      patternMismatch: false,
      rangeOverflow: false,
      rangeUnderflow: false,
      stepMismatch: false,
      tooLong: false,
      tooShort: false,
      typeMismatch: false,
      valid: true,
      valueMissing: false,
    };
  }

  constructor(form, options) {
    if (!isForm(form)) {
      throw new Error(FORM_REQUIRED);
    }

    this.form = form;
    this.options = options || {};

    this.addEventListeners = this.addEventListeners.bind(this);
    this.addOnBlur = this.addOnBlur.bind(this);
    this.addOnSubmit = this.addOnSubmit.bind(this);
    this.clearErrorMessage = this.clearErrorMessage.bind(this);
    this.getToken = this.getToken.bind(this);
    this.initialize = this.initialize.bind(this);
    this.markInputValid = this.markInputValid.bind(this);
    this.markInputInvalid = this.markInputInvalid.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.parseForm = this.parseForm.bind(this);
    this.putForm = this.putForm.bind(this);
    this.renderErrors = this.renderErrors.bind(this);
    this.setErrorMessage = this.setErrorMessage.bind(this);
    this.toPageTop = this.toPageTop.bind(this);
    this.validateField = this.validateField.bind(this);
    this.onClickCopeOtherRadioShowHideOtherDiv = this.onClickCopeOtherRadioShowHideOtherDiv.bind(this);
    this.addOnClickCopeOtherRadioShowHideOtherDiv = this.addOnClickCopeOtherRadioShowHideOtherDiv.bind(this);
  }

  initialize() {
    this.form.setAttribute(NO_VALIDATE_ATTR, true);
    this.addEventListeners();
  }

  addEventListeners() {
    this.addOnBlur();
    this.addOnSubmit();
    this.addOnClickCopeOtherRadioShowHideOtherDiv();
  }

  addOnBlur() {
    this.form.addEventListener(BLUR_EVENT, this.onBlur, true);
  }

  toPageTop() {
    window.scrollTo(0, 0);
  }

  onBlur(event) {
    const input = event.target;

    if (!isValidatableType(input.type)) {
      return;
    }

    const error = this.validateField(input);

    this.renderErrors([
      {
        error: error,
        input: input,
      },
    ]);

    const successMsg = document.getElementById(SUBMIT_SUCCESS_SELECTOR);
    this.removeClassName(successMsg, SUBMIT_SUCCESS_COMPLETE);

    if (!error) {
      this.putForm()
        .then((result) => {
          // console.log("FormValidator.onBlur() this.putForm() result: ", result);
        })
        .catch((err) => {
          // console.log("FormValidator.onBlur() this.putForm() error: ", err);
        });
    }
  }

  parseForm() {
    const data = {};
    const inputs = this.form.elements;

    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];
      const value = input.value;
      if (input.type === "radio") {
        if (input.checked) {
          data[input.name] = value;
        }
      } else {

        if (value) {
          data[input.id] = value;
        }
      }
    }

    return data;
  }

  async getToken() {
    const req = new Request(FormValidator.TOKEN_ENDPOINT);
    return await req.get();
  }

  async putForm(submitButton = false) {
    const data = this.parseForm();
    data.submitButton = submitButton;
    const token = await this.getToken();
    data._csrf = token;
    const req = new Request(FormValidator.FORM_SUBMIT_ENDPOINT);
    return await req.put(data);
  }

  addOnSubmit() {
    this.form.addEventListener(SUBMIT_EVENT, this.onSubmit, false);
  }

  onSubmit(event) {
    event.preventDefault();
    const { beforeSubmit, afterSubmit } = this.options;
    const fields = this.form.elements;
    const validations = [];

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      const error = this.validateField(field);

      validations.push({
        error: error,
        input: field,
      });
    }

    if (typeof beforeSubmit === "function") {
      try {
        beforeSubmit(validations, this.form);
      } catch (err) {
        // noop
      }
    }

    this.renderErrors(validations);

    const firstErrorIndex = validations.findIndex((v) => !!v.error);
    const hasErrors = firstErrorIndex > -1;

    if (hasErrors) {
      validations[firstErrorIndex].input.focus();
      return Promise.resolve(validations);
    }

    return this.putForm(true)
      .then(() => {
        try {
          afterSubmit();
        } catch (e) {
          // noop
        }

        this.form.reset();
        const errorMsg = document.getElementById(SUBMIT_ERROR_SELECTOR);
        this.removeClassName(errorMsg, SUBMIT_ERROR_HAS_ERROR);
        const successMsg = document.getElementById(SUBMIT_SUCCESS_SELECTOR);
        this.addClassName(successMsg, SUBMIT_SUCCESS_COMPLETE);
        successMsg.focus();
        this.toPageTop();
      })
      .catch((err) => {
        try {
          afterSubmit(err);
        } catch (e) {
          // noop
        }

        const errorMsg = document.getElementById(SUBMIT_ERROR_SELECTOR);
        this.addClassName(errorMsg, SUBMIT_ERROR_HAS_ERROR);
        errorMsg.focus();
        this.toPageTop();
      });
  }

  addClassName(elem, className) {
    const currentClass = elem.className;
    if (currentClass.indexOf(className) > -1) {
      return;
    }
    elem.className = `${currentClass} ${className}`.trim();
  }

  removeClassName(elem, className) {
    elem.className = elem.className.replace(className, EMPTY_MSG).trim();
  }

  markInputValid(input) {
    this.removeClassName(input, FormValidator.INPUT_ERROR_CLASS);
  }

  markInputInvalid(input) {
    this.addClassName(input, FormValidator.INPUT_ERROR_CLASS);
  }

  clearErrorMessage(field) {
    const id = field.id || field.name;
    const msgSelector = `.${FormValidator.ERROR_MESSAGE_CLASSNAME}#error-for-${id}`;
    const msgElem = this.form.querySelector(msgSelector);
    if (!msgElem) {
      console.log(
        `this.clearErrorMessage() cannot find "${id}" message element with selector "${msgSelector}". Field ${field.name}`
      );
      return;
    }
    field.removeAttribute(ARIA_DESC_BY);
    msgElem.innerHTML = EMPTY_MSG;
  }

  setErrorMessage(field, message) {
    const id = field.id || field.name;
    const msgSelector = `.${FormValidator.ERROR_MESSAGE_CLASSNAME}#error-for-${id}`;
    const msgElem = this.form.querySelector(msgSelector);
    if (!msgElem) {
      console.log(
        `this.setErrorMessage() cannot find "${id}" message element with selector "${msgSelector}"`
      );
      return;
    }
    field.setAttribute(ARIA_DESC_BY, `error-for-${id}`);
    msgElem.innerHTML = message;
  }

  renderErrors(fields) {
    if (!Array.isArray(fields)) {
      return;
    }

    fields.forEach((f) => {
      const field = f.input;
      const error = f.error;

      if (!isValidatableType(field.type)) {
        return;
      }

      if (error) {
        this.markInputInvalid(field);
        this.setErrorMessage(field, error.message);
      } else {
        this.markInputValid(field);
        this.clearErrorMessage(field);
      }
    });
  }

  validateField(field) {
    if (!field) {
      throw new Error(INVALID_FIELD);
    }

    if (field.disabled) {
      return null;
    }

    const fieldType = field.type;

    if (!isValidatableType(fieldType)) {
      return null;
    }

    const validity = isFunc(field.validate) ? field.validate() : field.validity;

    if (!validity) {
      throw new Error(CANNOT_VALIDATE);
    }

    if (validity.valid) {
      return null;
    }

    if (validity.valueMissing) {
      return new Error(VALUE_MISSING_ERR);
    }

    if (validity.typeMismatch) {
      if (fieldType === EMAIL_TYPE) {
        return new Error(EMAIL_TYPE_MISMATCH);
      }
    }

    if (validity.tooShort) {
      const minLength = field.getAttribute(MIN_LENGTH);
      const errMsg = `Value is too short. Please enter at least ${minLength} characters.`;
      return new Error(errMsg);
    }

    if (validity.tooLong) {
      const maxLength = field.getAttribute(MAX_LENGTH);
      const errMsg = `Value is too long. Please enter no more than ${maxLength} characters.`;
      return new Error(errMsg);
    }

    // this applies to [type="number"]
    if (validity.badInput) {
      return new Error(NUMERIC_ONLY);
    }

    // If a number value doesn't match the step interval
    // if (validity.stepMismatch) return "Please select a valid value.";

    // If a number field is over the max
    // if (validity.rangeOverflow)
    //   return (
    //     "Please select a value that is no more than " +
    //     field.getAttribute("max") +
    //     "."
    //   );

    // If a number field is below the min
    // if (validity.rangeUnderflow)
    //   return (
    //     "Please select a value that is no less than " +
    //     field.getAttribute("min") +
    //     "."
    //   );

    if (validity.patternMismatch) {
      if (field.hasAttribute(TITLE_ATTR)) {
        return new Error(field.getAttribute(TITLE_ATTR));
      }

      return new Error(PATTERN_MISMATCH);
    }

    return new Error(INVALID_VALUE);
  }

  addOnClickCopeOtherRadioShowHideOtherDiv() {
    this.form.addEventListener(CLICK_EVENT, this.onClickCopeOtherRadioShowHideOtherDiv, false);
  }

  onClickCopeOtherRadioShowHideOtherDiv(event) {
    const target = event ? event.target : null;
    if (target && target.type ===  "radio") {      
      const div = document.getElementById('other_div');
      if (div) { 
        if (target.id === "copeOther") {                  
          div.style.display =  'block';
        } else if (target.id.startsWith("cope")) {
          div.style.display = 'none';
        } 
      } 
    }
  }
}
